<script lang="ts">
  import { onMount } from 'svelte';
  import type { Pattern } from '../pipeline/pipeline';
  import type { BeadColor } from '../catalog/types';
  import type { Viewport } from '../render/types';
  import {
    computeThumbDimensions,
    computeViewportRect,
    isPatternFullyVisible,
    thumbToCell,
  } from '../render/minimap';

  /**
   * Mini-map : aperçu thumbnail du pattern + indicateur de la zone visible.
   * Cf. §5 du cahier des charges (orientation grandes œuvres > 100×100).
   *
   * Caching : la thumbnail à résolution native du pattern est rendue une seule
   * fois dans un OffscreenCanvas (ou fallback HTMLCanvasElement), réutilisée
   * tant que `pattern` ne change pas. Sur 300×300 cellules : 90 000 pixels
   * écrits dans une `ImageData` en une passe, puis `drawImage` avec scaling
   * pour le rendu final.
   */

  type Props = {
    pattern: Pattern;
    palette: ReadonlyArray<BeadColor>;
    viewport: Viewport;
    containerSize: { w: number; h: number };
    cellSize: number;
    /** Largeur max du thumb en px CSS. Hauteur déduite via aspect ratio. */
    maxSize?: number;
    /** Callback appelé quand l'utilisateur clique/glisse pour recentrer. */
    onPan?: (col: number, row: number) => void;
  };

  let {
    pattern,
    palette,
    viewport,
    containerSize,
    cellSize,
    maxSize = 160,
    onPan,
  }: Props = $props();

  let canvasEl: HTMLCanvasElement | undefined = $state();

  // Cache de la thumbnail à résolution native (1 px par cellule).
  // Clé : la référence de `pattern.cells` (changée à chaque paintCellsBatch).
  // Plus fiable que la ref de `pattern` car c'est la donnée qu'on rend,
  // et le tracking Svelte 5 est fine-grained sur la propriete.
  let cachedOffscreen: HTMLCanvasElement | null = null;
  let cachedCellsRef: Pattern['cells'] | null = null;

  let dims = $derived(computeThumbDimensions(pattern.width, pattern.height, maxSize));
  let vpRect = $derived(computeViewportRect(viewport, containerSize, cellSize, dims.cellPx));
  let hidden = $derived(
    isPatternFullyVisible(viewport, containerSize, cellSize, pattern.width, pattern.height),
  );

  function buildOffscreen(): HTMLCanvasElement | null {
    if (typeof document === 'undefined') return null;
    const W = pattern.width;
    const H = pattern.height;
    if (W <= 0 || H <= 0) return null;
    const off = document.createElement('canvas');
    off.width = W;
    off.height = H;
    const ctx = off.getContext('2d');
    if (!ctx) return null;

    // Une seule passe : remplit un Uint8ClampedArray, puis putImageData.
    // ~5 ns par cellule sur un i5, donc 90k cellules = 0.5 ms.
    const buf = new Uint8ClampedArray(W * H * 4);
    const cells = pattern.cells;
    for (let i = 0; i < cells.length; i++) {
      const idx = cells[i];
      if (idx === undefined) continue;
      const bead = palette[idx];
      if (!bead) continue;
      const o = i * 4;
      buf[o] = bead.rgb.r;
      buf[o + 1] = bead.rgb.g;
      buf[o + 2] = bead.rgb.b;
      buf[o + 3] = 255;
    }
    ctx.putImageData(new ImageData(buf, W, H), 0, 0);
    return off;
  }

  function draw(): void {
    if (!canvasEl || dims.thumbW === 0) return;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;

    canvasEl.width = dims.thumbW;
    canvasEl.height = dims.thumbH;
    // Explicite : setting width clear deja, mais on garantit l'etat propre
    // au cas ou le canvas vient juste d'etre remonte (sortie du {#if !hidden}).
    ctx.clearRect(0, 0, dims.thumbW, dims.thumbH);

    if (cachedCellsRef !== pattern.cells || cachedOffscreen === null) {
      cachedOffscreen = buildOffscreen();
      cachedCellsRef = pattern.cells;
    }
    if (!cachedOffscreen) return;

    // Rendu de la thumbnail (scaled, nearest-neighbor pour préserver les pixels)
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(cachedOffscreen, 0, 0, dims.thumbW, dims.thumbH);

    // Overlay sombre HORS de la zone visible (clip via rect "evenodd")
    const x = Math.max(0, vpRect.x);
    const y = Math.max(0, vpRect.y);
    const w = Math.min(dims.thumbW, vpRect.x + vpRect.w) - x;
    const h = Math.min(dims.thumbH, vpRect.y + vpRect.h) - y;
    if (w > 0 && h > 0) {
      ctx.save();
      ctx.fillStyle = 'rgba(32, 49, 65, 0.45)';
      // Tracé du thumb complet PUIS du rect viewport ; fillRule evenodd remplit
      // la zone entre les deux = tout sauf le rect visible.
      ctx.beginPath();
      ctx.rect(0, 0, dims.thumbW, dims.thumbH);
      ctx.rect(x, y, w, h);
      ctx.fill('evenodd');
      ctx.restore();

      // Cadre accent autour de la zone visible
      ctx.save();
      const accent = readAccent();
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      ctx.restore();
    } else {
      // Le viewport est entièrement hors-pattern : on assombrit tout
      ctx.save();
      ctx.fillStyle = 'rgba(32, 49, 65, 0.45)';
      ctx.fillRect(0, 0, dims.thumbW, dims.thumbH);
      ctx.restore();
    }
  }

  function readAccent(): string {
    if (typeof document === 'undefined') return '#B8843F';
    const cs = getComputedStyle(document.documentElement);
    return cs.getPropertyValue('--color-accent').trim() || '#B8843F';
  }

  let rafId = 0;
  function schedule(): void {
    if (rafId !== 0) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      draw();
    });
  }

  // --- Drag pour recentrer le viewport principal -------------------------

  let isDragging = false;

  function emitPanFromEvent(e: MouseEvent | Touch): void {
    if (!canvasEl || !onPan) return;
    const r = canvasEl.getBoundingClientRect();
    const tx = ((e.clientX - r.left) / r.width) * dims.thumbW;
    const ty = ((e.clientY - r.top) / r.height) * dims.thumbH;
    const { col, row } = thumbToCell(tx, ty, dims.cellPx, pattern.width, pattern.height);
    onPan(col, row);
  }

  function onMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return;
    isDragging = true;
    emitPanFromEvent(e);
  }
  function onMouseMove(e: MouseEvent): void {
    if (!isDragging) return;
    emitPanFromEvent(e);
  }
  function onMouseUp(): void {
    isDragging = false;
  }

  function onTouchStart(e: TouchEvent): void {
    const t = e.touches[0];
    if (!t) return;
    e.preventDefault();
    isDragging = true;
    emitPanFromEvent(t);
  }
  function onTouchMove(e: TouchEvent): void {
    if (!isDragging) return;
    const t = e.touches[0];
    if (!t) return;
    e.preventDefault();
    emitPanFromEvent(t);
  }
  function onTouchEnd(): void {
    isDragging = false;
  }

  onMount(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  });

  $effect(() => {
    // Dependance explicite sur `cells` (l'array passe par paintCellsBatch
    // qui retourne une nouvelle reference). C'est plus robuste que
    // `void pattern` qui ne track que l'identite de l'objet et peut rater
    // certains cas (notamment apres un re-mount du canvas suite a {#if hidden}).
    void pattern.cells;
    void pattern.width;
    void pattern.height;
    void viewport;
    void containerSize;
    void dims;
    schedule();
  });
</script>

{#if !hidden}
  <div
    class="minimap"
    style={`width: ${dims.thumbW}px; height: ${dims.thumbH}px`}
    role="img"
    aria-label="Aperçu du patron — cliquez pour vous déplacer"
  >
    <canvas
      bind:this={canvasEl}
      onmousedown={onMouseDown}
      ontouchstart={onTouchStart}
      ontouchmove={onTouchMove}
      ontouchend={onTouchEnd}
      ontouchcancel={onTouchEnd}
    ></canvas>
  </div>
{/if}

<style>
  .minimap {
    position: absolute;
    bottom: var(--space-3);
    right: var(--space-3);
    background: var(--color-background);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-md);
    overflow: hidden;
    /* Au-dessus du canvas, mais sous d'éventuels overlays applicatifs */
    z-index: 10;
    touch-action: none;
  }
  canvas {
    display: block;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
  /* Sur écrans étroits : repositionner en haut-droite pour ne pas chevaucher
     le MobileDock qui occupe le bas du canvas, et réduire la taille pour
     laisser de la place à l'œuvre. */
  @media (max-width: 768px) {
    .minimap {
      bottom: auto;
      top: var(--space-2);
      right: var(--space-2);
      transform: scale(0.7);
      transform-origin: top right;
    }
  }
</style>
